'use strict'

import $ from 'jquery'

import { selectors } from '../selectors'
import { selectorsCustomer } from '../selectors_customer'
import { input, rule, flag, callback } from './enums'
import regex from './regex'
import mask from './masks'
import { emailCustom, employmentCustom, repayOtherCustom, checkInputLimit, earlySettlementOtherCustom, earlySettlementReasonCustom, testAgainstMultiple, addressCustom, addressCustomInputValidation, confirmPasswordMatchCustom } from './rulesetCustom'
import { ATTR, scrollIntoView, validateCheckBox, validateDate, validateInput, validateRadio, validateRange, validateSelect, validateTextArea } from './helpers'
import { elementIsCheckbox, elementIsDate, elementIsInput, elementIsRadio, elementIsRange, elementIsSelect, elementIsTextArea, $enableElement } from '../shared'
import { emailExternal, phoneExternal, personExternal, cardValidationExternal, reCaptchaExternal } from './rulesetExternal'

export const event = {
    [input.submit]: 'click',
    [input.checkbox]: 'change',
    [input.date]: 'paste change',
    [input.radio]: 'change',
    [input.range]: 'change',
    [input.select]: 'change',
    [input.text]: 'paste change',
    [input.file]: null,
    [input.textarea]: 'change',
}

export const errorEvent = {
    [input.submit]: null,
    [input.checkbox]: null,
    [input.date]: 'keyup',
    [input.radio]: null,
    [input.range]: null,
    [input.select]: null,
    [input.text]: 'keyup',
    [input.file]: null,
    [input.textarea]: 'keyup',
}

export const type = {
    [input.checkbox]: elementIsCheckbox,
    [input.date]: elementIsDate,
    [input.radio]: elementIsRadio,
    [input.range]: elementIsRange,
    [input.select]: elementIsSelect,
    [input.text]: elementIsInput,
    [input.file]: () => {},
    [input.textarea]: elementIsTextArea,
}

export const fn = {
    [input.checkbox]: validateCheckBox,
    [input.date]: validateDate,
    [input.radio]: validateRadio,
    [input.range]: validateRange,
    [input.select]: validateSelect,
    [input.text]: validateInput,
    [input.file]: '...TODO...',
    [input.textarea]: validateTextArea,
}

export const ruleset = {
    // ------------------------ Login ------------------------

    // These two forms have never had client-side field validation and this is not the ticket to add
    // it, so reCAPTCHA is their only rule. It is an external rule on the hidden token field, the same
    // shape as the Acquired card widget, so the token is minted at submit time (UKS-1692).
    [selectors.submits.login]: {
        description: 'Client login - reCAPTCHA only',
        callbacks: {},
        inputs: {
            [selectors.inputs.loginReCaptcha]: {
                [rule.external]: reCaptchaExternal,
                [flag.forceValidation]: true,
            },
        },
    },

    // ------------------- Password reset --------------------

    [selectors.submits.resetPassword]: {
        description: 'Forgotten password - reCAPTCHA only',
        callbacks: {},
        inputs: {
            [selectors.inputs.resetPasswordReCaptcha]: {
                [rule.external]: reCaptchaExternal,
                [flag.forceValidation]: true,
            },
            [selectors.inputs.resetPasswordEmail]: {
                [rule.regex]: regex.email,
                [rule.custom]: emailCustom,
            },
        },
    },

    // ----------------------- Contact -----------------------

    [selectors.submits.contact]: {
        description: 'Contact us',
        callbacks: {},
        inputs: {
            [selectors.inputs.contactReCaptcha]: {
                [rule.external]: reCaptchaExternal,
                [flag.forceValidation]: true,
            },
            [selectors.inputs.contactName]: {
                [rule.custom]: testAgainstMultiple,
            },
            [selectors.inputs.contactSurname]: {
                [rule.custom]: testAgainstMultiple,
            },
            [selectors.inputs.contactEmail]: {
                [rule.regex]: regex.email,
                [rule.custom]: emailCustom,
            },
            [selectors.inputs.contactPhone]: {
                [rule.mask]: mask.cellPhoneNumber,
                // [rule.regex]: regex.phoneNumber,
            },
            [selectors.inputs.contactMessage]: {
                [rule.regex]: regex.notEmpty,
            },
        },
    },

    // --------------------- Registration ---------------------

    [selectors.submits.accountInfo]: {
        description: 'RegistrationForm Step1 - AccountInfo',
        callbacks: {
            [callback.onError]: ($inputs) => {
                scrollIntoView($inputs)
            },
        },
        inputs: {
            [selectors.inputs.accountInfoReCaptcha]: {
                [rule.external]: reCaptchaExternal,
                [flag.forceValidation]: true,
            },
            [selectors.selects.title]: {
                [rule.selected]: true,
            },
            [selectors.inputs.name]: {
                [rule.custom]: testAgainstMultiple,
                [rule.external]: personExternal,
            },
            [selectors.inputs.surname]: {
                [rule.custom]: testAgainstMultiple,
                [rule.external]: personExternal,
            },
            [selectors.selects.maritalStatus]: {
                [rule.selected]: true,
            },
            [selectors.inputs.dateOfBirth]: {
                [rule.regex]: regex.date,
                [rule.external]: personExternal,
                [flag.forceValidation]: true,
            },
            [selectors.inputs.email]: {
                [rule.regex]: regex.email,
                [rule.custom]: emailCustom,
                [rule.external]: emailExternal,
            },
            [selectors.inputs.phoneNumber]: {
                [rule.mask]: mask.cellPhoneNumber,
                [rule.regex]: regex.cellPhoneNumber,
                [rule.external]: phoneExternal,
            },
            [selectors.inputs.password]: {
                [rule.regex]: regex.password,
            },
            [selectors.inputs.confirmPassword]: {
                [rule.custom]: confirmPasswordMatchCustom,
            },
            [selectors.checkboxes.process]: {
                [rule.selected]: true,
                [flag.forceValidation]: true,
            },
        },
    },
    [selectors.submits.address]: {
        description: 'RegistrationForm Step2 - Address',
        callbacks: {
            [callback.onError]: ($inputs) => {
                scrollIntoView($inputs)
            },
        },
        inputs: {
            [selectors.inputs.streetNumber]: {
                [rule.custom]: addressCustomInputValidation,
            },

            [selectors.inputs.houseName]: {
                [rule.custom]: addressCustomInputValidation,
            },
            [selectors.inputs.flatNumber]: {
                [rule.regex]: regex.flatNumber,
            },
            [selectors.inputs.city]: {
                [rule.regex]: regex.city,
            },
            [selectors.inputs.street]: {
                [rule.regex]: regex.street,
            },
            [selectors.inputs.county]: {
                [rule.regex]: regex.county,
            },
            [selectors.inputs.zipCode]: {
                [rule.mask]: mask.zipCode,
            },
            [selectors.selects.homeOwnerStatus]: {
                [rule.selected]: true,
            },
            [selectors.selects.timeAtAddress]: {
                [rule.selected]: true,
            },
            [selectors.selects.typeOfHousing]: {
                [rule.selected]: true,
            },
            [selectors.inputs.peopleSplitRent]: {
                [rule.regex]: regex.peopleSplitRent,
            },
        },
    },
    [selectors.submits.employment]: {
        description: 'RegistrationForm Step3 - Employment',
        callbacks: {
            [callback.onError]: ($inputs) => {
                scrollIntoView($inputs)
            },
        },
        inputs: {
            [selectors.selects.employmentStatus]: {
                [rule.selected]: true,
            },
            [selectors.inputs.employerName]: {
                [rule.regex]: regex.employer,
            },
            [selectors.inputs.employerPhoneNumber]: {
                [rule.mask]: mask.employerPhoneNumber,
                // [rule.regex]: regex.employerPhoneNumber,
            },
            [selectors.selects.workingYears]: {
                [rule.selected]: true,
            },
            [selectors.inputs.monthlyIncome]: {
                [rule.mask]: mask.income,
                [rule.regex]: regex.income,
                [rule.custom]: employmentCustom,
            },
            [selectors.inputs.totalMonthlyIncome]: {
                [rule.mask]: mask.income,
                [rule.regex]: regex.income,
                [rule.custom]: employmentCustom,
            },
            [selectors.selects.salaryPeriodicity]: {
                [rule.selected]: true,
            },
            [selectors.selects.nameOfDayPaid]: {
                [rule.selected]: true,
            },
            [selectors.inputs.dateOfDayPaid]: {
                [rule.regex]: regex.dateOfDayPaid,
            },
            [selectors.inputs.rentMortgage]: {
                [rule.mask]: mask.income,
                [rule.regex]: regex.cost,
                // [rule.mask]: mask.costs,
            },
            [selectors.inputs.utilities]: {
                [rule.mask]: mask.income,
                [rule.regex]: regex.cost,
            },
            [selectors.inputs.loanPayments]: {
                [rule.mask]: mask.income,
                [rule.regex]: regex.cost,
            },
            [selectors.inputs.transport]: {
                [rule.mask]: mask.income,
                [rule.regex]: regex.cost,
            },
            [selectors.inputs.otherHouseholdExpenses]: {
                [rule.mask]: mask.income,
                [rule.regex]: regex.cost,
            },
            [selectors.inputs.food]: {
                [rule.mask]: mask.income,
                [rule.regex]: regex.cost,
            },
            [selectors.inputs.numberOfDependents]: {
                [rule.mask]: mask.income,
                [rule.regex]: regex.dependants,
            },
            [selectors.checkboxes.confirmDetails]: {
                [rule.selected]: true,
            },
        },
    },
    [selectors.submits.vulnerability]: {
        description: 'RegistrationForm Step4 - Vulnerabilities',
        callbacks: {
            [callback.onError]: ($inputs) => {
                scrollIntoView($inputs)
            },
        },
        inputs: {
            [selectors.selects.loanReason]: {
                [rule.selected]: true,
            },
            [selectors.selects.healthConsiderations]: {
                [rule.selected]: true,
            },
            [selectors.selects.lifeEvents]: {
                [rule.selected]: true,
            },
            [selectors.selects.financialSupport]: {
                [rule.selected]: true,
            },
            [selectors.selects.financialResilience]: {
                [rule.selected]: true,
            },
            [selectors.selects.capabilityIssues]: {
                [rule.selected]: true,
            },
            [selectors.selects.financialKnowledgeSupport]: {
                [rule.selected]: true,
            },
            [selectors.selects.priorityBillsUpToDate]: {
                [rule.selected]: true,
            },
        },
    },

    // --------------- Simple Registration --------------

    [selectors.submits.simplePassword]: {
        description: 'Simple Registration - Password',
        callbacks: {},
        inputs: {
            [selectors.inputs.simplePassword]: {
                [rule.regex]: regex.password,
            },
            [selectors.inputs.simpleConfirmPassword]: {
                [rule.custom]: confirmPasswordMatchCustom,
            },
        },
    },

    // --------------- Password Change (Client) --------------

    [selectors.submits.changePassword]: {
        description: 'Client - Change Password',
        callbacks: {},
        inputs: {
            [selectors.inputs.changePasswordNew]: {
                [rule.regex]: regex.password,
            },
            [selectors.inputs.changePasswordConfirm]: {
                [rule.custom]: confirmPasswordMatchCustom,
            },
        },
    },

    // --------------- Password Reset (set new) --------------

    [selectors.submits.changePasswordFromReset]: {
        description: 'Client - Change Password From Reset',
        callbacks: {},
        inputs: {
            [selectors.inputs.changePasswordFromResetNew]: {
                [rule.regex]: regex.password,
            },
            [selectors.inputs.changePasswordFromResetConfirm]: {
                [rule.custom]: confirmPasswordMatchCustom,
            },
        },
    },

    // ------------------------ Shared ------------------------

    [selectors.submits.cardDetails]: {
        description: 'CardDetails + BankAccount',
        callbacks: {
            [callback.onError]: ($inputs) => {
                scrollIntoView($inputs)
            },
        },
        inputs: {
            [selectors.inputs.bankAccountNumber]: {
                [rule.mask]: mask.bankAccountNumber,
            },
            [selectors.inputs.bankSortCode]: {
                [rule.mask]: mask.bankSortCode,
            },
            [selectors.inputs.cardDetails]: {
                [rule.external]: cardValidationExternal,
                [flag.forceValidation]: true,
            },
            [selectors.checkboxes.cardStoreApprove]: {
                [rule.selected]: true,
                [flag.forceValidation]: true,
            },
        },
    },

    // -------------------- Client - LoanDC --------------------

    [selectorsCustomer.submits.promiseToPay]: {
        description: 'LoanDC - Promise to pay',
        callbacks: {},
        inputs: {
            [selectorsCustomer.inputs.promiseToPayDate]: {
                [rule.regex]: regex.date,
                [flag.forceValidation]: true,
            },
        },
    },
    [selectorsCustomer.submits.reducedPlanStep1]: {
        description: 'Reduced payment plan - Step 1',
        callbacks: {},
        inputs: {
            [selectorsCustomer.inputs.reducedPlanPayment]: {
                [rule.regex]: regex.notEmptyNumberAndZero,
                [rule.external]: checkInputLimit,
            },
        },
    },
    [selectorsCustomer.submits.reducedPlanStep2]: {
        description: 'Reduced payment plan - Step 2',
        callbacks: {},
        inputs: {
            [selectorsCustomer.inputs.reducedPlanIncome]: {
                [rule.regex]: regex.notEmptyNumberAndZero,
            },
            [selectorsCustomer.inputs.reducedPlanIncomeOther]: {
                [rule.regex]: regex.notEmptyNumber,
            },
            [selectorsCustomer.inputs.reducedPlanCostHousing]: {
                [rule.regex]: regex.notEmptyNumber,
            },
            [selectorsCustomer.inputs.reducedPlanCostUtils]: {
                [rule.regex]: regex.notEmptyNumber,
            },
            [selectorsCustomer.inputs.reducedPlanCostCouncil]: {
                [rule.regex]: regex.notEmptyNumber,
            },
            [selectorsCustomer.inputs.reducedPlanCostCommitments]: {
                [rule.regex]: regex.notEmptyNumber,
            },
            [selectorsCustomer.inputs.reducedPlanCostTravel]: {
                [rule.regex]: regex.notEmptyNumber,
            },
            [selectorsCustomer.inputs.reducedPlanCostSubscription]: {
                [rule.regex]: regex.notEmptyNumber,
            },
            [selectorsCustomer.inputs.reducedPlanCostHousehold]: {
                [rule.regex]: regex.notEmptyNumber,
            },
            [selectorsCustomer.inputs.reducedPlanCostOther]: {
                [rule.regex]: regex.notEmptyNumber,
            },
            [selectorsCustomer.inputs.reducedPlanDependants]: {
                [rule.regex]: regex.notEmptyNumber,
            },
        },
    },
    [selectorsCustomer.submits.cardCpaDisable]: {
        description: 'Card CPA Disable',
        callbacks: {},
        inputs: {
            [selectorsCustomer.checkboxes.cardCpaDisableConfirm]: {
                [rule.selected]: true,
                [flag.forceValidation]: true,
            },
        },
    },

    // -------------------- Client --------------------

    [selectorsCustomer.submits.accountInfoUpdateSubmit]: {
        description: 'Customer Account Info validation',
        callbacks: {},
        inputs: {
            [selectorsCustomer.selects.customerTitle]: {
                [rule.selected]: true,
            },
            [selectorsCustomer.selects.customerFirstName]: {
                [rule.custom]: testAgainstMultiple,
            },
            [selectorsCustomer.selects.customerLastName]: {
                [rule.custom]: testAgainstMultiple,
            },
            [selectorsCustomer.selects.customerDateOfBirth]: {
                [rule.regex]: regex.date,
            },
            [selectorsCustomer.selects.customerEmail]: {
                [rule.regex]: regex.email,
            },
            [selectorsCustomer.inputs.customerPhoneNumber]: {
                [rule.mask]: mask.cellPhoneNumber,
                [rule.regex]: regex.cellPhoneNumber,
            },
            [selectorsCustomer.selects.customerMaritalStatus]: {
                [rule.selected]: true,
            },
            [selectorsCustomer.checkboxes.customerProcessDataApprove]: {
                [rule.selected]: true,
                [flag.forceValidation]: true,
            },
        },
    },
    [selectorsCustomer.submits.addressUpdateSubmit]: {
        description: 'Customer Address validation',
        callbacks: {},
        inputs: {
            [selectorsCustomer.selects.customerHomeOwnerStatus]: {
                [rule.selected]: true,
            },
            [selectorsCustomer.inputs.customerAddressInfoCounty]: {
                [rule.regex]: regex.county,
            },
            [selectorsCustomer.inputs.customerAddressInfoCity]: {
                [rule.regex]: regex.city,
            },
            [selectorsCustomer.inputs.customerAddressInfoStreet]: {
                [rule.regex]: regex.street, // ToDo: implement the address helper
            },
            [selectorsCustomer.inputs.customerAddressInfoStreetNumber]: {
                [rule.custom]: addressCustomInputValidation,
            },
            [selectorsCustomer.inputs.customerAddressInfoHouseName]: {
                [rule.custom]: addressCustomInputValidation,
            },
            [selectorsCustomer.inputs.customerAddressInfoFlatNumber]: {
                [rule.regex]: regex.flatNumber,
            },
            [selectorsCustomer.inputs.customerAddressInfoZipCode]: {
                [rule.mask]: mask.zipCode,
            },
            [selectorsCustomer.selects.customerTimeAtAddress]: {
                [rule.selected]: true,
            },
            [selectorsCustomer.selects.customerTypeOfHousing]: {
                [rule.selected]: true,
            },
            [selectorsCustomer.inputs.customerPeopleSplitRent]: {
                [rule.regex]: regex.peopleSplitRent,
            },
        },
    },
    [selectorsCustomer.submits.employmentUpdateSubmit]: {
        description: 'Customer Employment validation',
        callbacks: {},
        inputs: {
            [selectorsCustomer.selects.customerEmploymentStatus]: {
                [rule.selected]: true,
            },
            [selectorsCustomer.selects.customerEmploymentWorkingYears]: {
                [rule.selected]: true,
            },
            [selectorsCustomer.selects.customerEmploymentSalaryPeriodicity]: {
                [rule.selected]: true,
            },
            [selectorsCustomer.inputs.customerEmploymentDateOfDayPaid]: {
                [rule.selected]: true,
            },
            [selectorsCustomer.selects.customerEmploymentNameOfDayPaid]: {
                [rule.selected]: true,
            },
            [selectorsCustomer.inputs.customerEmploymentEmployerName]: {
                [rule.regex]: regex.employer,
            },
            [selectorsCustomer.inputs.customerEmploymentEmployerPhoneNumber]: {
                [rule.mask]: mask.employerPhoneNumber,
                // [rule.regex]: regex.employerPhoneNumber,
                // [rule.external]: phoneExternal,
            },
            [selectorsCustomer.inputs.customerEmploymentMonthlyIncome]: {
                [rule.regex]: regex.income,
                // [rule.custom]: employmentCustom,
            },
            [selectorsCustomer.inputs.customerEmploymentTotalMonthlyIncome]: {
                [rule.regex]: regex.income,
                // [rule.custom]: employmentCustom,
            },
            [selectorsCustomer.selects.customerEmploymentSalaryPeriodicity]: {
                [rule.selected]: true,
            },
            [selectorsCustomer.inputs.customerEmploymentRentMortgage]: {
                [rule.regex]: regex.cost,
            },
            [selectorsCustomer.inputs.customerEmploymentUtilities]: {
                [rule.regex]: regex.cost,
            },
            [selectorsCustomer.inputs.customerEmploymentLoanPayments]: {
                [rule.regex]: regex.cost,
            },
            [selectorsCustomer.inputs.customerEmploymentTransport]: {
                [rule.regex]: regex.cost,
            },
            [selectorsCustomer.inputs.customerEmploymentOtherHouseholdExpenses]: {
                [rule.regex]: regex.cost,
            },
            [selectorsCustomer.inputs.customerEmploymentFood]: {
                [rule.regex]: regex.cost,
            },
            [selectorsCustomer.inputs.customerEmploymentNumberOfDependents]: {
                [rule.regex]: regex.cost,
            },
        },
    },
    [selectorsCustomer.submits.vulnerabilityUpdateSubmit]: {
        description: 'Customer Vulnerability validation',
        callbacks: {},
        inputs: {
            [selectorsCustomer.selects.customerVulnerabilityLoanReason]: {
                [rule.selected]: true,
            },
            [selectorsCustomer.selects.customerVulnerabilityHealthConsiderations]: {
                [rule.selected]: true,
            },
            [selectorsCustomer.selects.customerVulnerabilityLifeEvents]: {
                [rule.selected]: true,
            },
            [selectorsCustomer.selects.customerVulnerabilityFinancialSupport]: {
                [rule.selected]: true,
            },
            [selectorsCustomer.selects.customerVulnerabilityFinancialResilience]: {
                [rule.selected]: true,
            },
            [selectorsCustomer.selects.customerVulnerabilityOtherConsiderations]: {
                [rule.selected]: true,
            },
            [selectorsCustomer.selects.customerVulnerabilityCapabilityIssues]: {
                [rule.selected]: true,
            },
            [selectorsCustomer.selects.customerVulnerabilityFinancialKnowledgeSupport]: {
                [rule.selected]: true,
            },
            [selectorsCustomer.selects.customerVulnerabilityPriorityBillsUpToDate]: {
                [rule.selected]: true,
            },
        },
    },

    [selectorsCustomer.submits.cardPaymentPay]: {
        description: 'Card Payment Submit',
        callbacks: {
            [callback.onError]: () => {
                $enableElement($(selectorsCustomer.submits.cardPaymentPay))
            },
        },
        inputs: {
            [selectorsCustomer.inputs.cardPaymentPayment]: {
                [rule.external]: cardValidationExternal,
                [flag.forceValidation]: true,
            },
        },
    },
    [selectorsCustomer.submits.paymentAmount]: {
        description: 'Repay Other Submit',
        callbacks: {},
        inputs: {
            [selectorsCustomer.inputs.paymentAmount]: {
                [rule.mask]: mask.onlyNumbers,
                [rule.regex]: regex.notEmptyDecimalNumber,
                [rule.custom]: repayOtherCustom,
            },
        },
    },

    [selectorsCustomer.submits.earlySettlementReason]: {
        description: 'Early settlement reason',
        callbacks: {},
        inputs: {
            [selectorsCustomer.selects.earlySettlementReason]: {
                [rule.custom]: earlySettlementReasonCustom,
                [rule.selected]: true,
                [flag.forceValidation]: true,
            },
            [selectorsCustomer.inputs.earlySettlementOther]: {
                [rule.custom]: earlySettlementOtherCustom,
            },
        },
    },

    // ---------------------- Unsubscribe ----------------------

    [selectors.submits.unsubscribePhone]: {
        description: 'Unsubscribe SMS',
        callbacks: {},
        inputs: {
            [selectors.inputs.unsubscribePhone]: {
                [rule.mask]: mask.cellPhoneNumber,
                [rule.regex]: regex.cellPhoneNumber,
            },
        },
    },
    [selectors.submits.unsubscribeEmail]: {
        description: 'Unsubscribe Email',
        callbacks: {},
        inputs: {
            [selectors.inputs.unsubscribeEmail]: {
                [rule.regex]: regex.email,
                [rule.custom]: emailCustom,
            },
        },
    },

    // -------------------- Reject Consent --------------------

    // [selectors.submits.rejectConsentContinue]: {
    //     description: 'Reject - consent to share with partners',
    //     callbacks: {},
    //     inputs: {
    //         [selectors.checkboxes.rejectConsent]: {
    //             [rule.selected]: true,
    //             [flag.forceValidation]: true,
    //         },
    //     },
    // },

    // --------------------- TODO ---------------------
}
